import React, { MouseEventHandler, ReactNode } from 'react';

import { twMerge } from 'tailwind-merge';

import { setPreventUnload } from 'flow/actions/app-actions';
import BaseButton from './BaseButton';
import Icon, { IconKey } from './Icon';

type Props = {
    description?: string;
    iconKey?: IconKey;
    className?: string;
    children?: ReactNode;
} & (
    | { href: string; onClick?: never }
    | { onClick: MouseEventHandler<HTMLButtonElement>; href?: never }
);

// Stacked GeneralLinks touch, so the focused one is lifted above its
// neighbours: otherwise the next one's background covers the bottom of its
// focus ring.
const baseClassName =
    'relative flex w-full items-center justify-between rounded-sm p-2 text-start hover:bg-gray-100 focus-visible:z-10';

// With `href`, a link that opens in a new window; with `onClick`, a button for
// an action. Both are native elements, so they are in the Tab order and the
// keyboard activates them. Only phrasing content (spans) may go inside a button.
const GeneralLink = ({
    description,
    iconKey,
    className,
    children,
    ...props
}: Props) => {
    const content = (
        <>
            <span>{children}</span>
            <span className="flex items-center gap-x-1">
                <span className="text-sm text-gray-500">{description}</span>
                {iconKey ? <Icon iconKey={iconKey} /> : null}
            </span>
        </>
    );

    if (props.href !== undefined) {
        return (
            <a
                href={props.href}
                target="_blank"
                rel="noreferrer"
                className={twMerge('focus-ring', baseClassName, className)}
                // Skips the clearState on beforeunload; see TicketsIntroView.
                onClick={() => setPreventUnload(true)}
            >
                {content}
            </a>
        );
    }

    return (
        <BaseButton
            type="button"
            className={twMerge(baseClassName, className)}
            onClick={props.onClick}
        >
            {content}
        </BaseButton>
    );
};

export default GeneralLink;
